import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { Disc3, Trash2, Plus, Loader2, Upload, Image as ImageIcon, Music, ChevronDown, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const EMPTY_ALBUM = {
  title: "",
  artist: "VINCE SINNOTT",
  release_date: new Date().toISOString().slice(0, 10),
  cover_art_url: "",
  description: "",
  genre: "Electronic",
  label: "GODMODE SOUND",
  catalog_number: "",
  album_type: "album",
  status: "released",
  buy_url: "",
  tuning_hz: 432.0,
  tags: [],
};

const EMPTY_TRACK = {
  title: "",
  track_number: 1,
  audio_url: "",
  duration_seconds: 0,
  frequency_hz: null,
};

function AlbumForm({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-cover`, fd, { withCredentials: true });
      onChange({ ...value, cover_art_url: res.data.url });
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="album-form">
      <div className="md:col-span-2">
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Title *</label>
        <Input
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          placeholder="Album title"
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-title-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Artist</label>
        <Input
          value={value.artist}
          onChange={(e) => onChange({ ...value, artist: e.target.value })}
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-artist-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Release Date</label>
        <Input
          type="date"
          value={value.release_date}
          onChange={(e) => onChange({ ...value, release_date: e.target.value })}
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-release-date-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Label</label>
        <Input
          value={value.label}
          onChange={(e) => onChange({ ...value, label: e.target.value })}
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-label-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Genre</label>
        <Input
          value={value.genre}
          onChange={(e) => onChange({ ...value, genre: e.target.value })}
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-genre-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Album Type</label>
        <select
          value={value.album_type}
          onChange={(e) => onChange({ ...value, album_type: e.target.value })}
          className="w-full bg-black border border-[#FF0000]/30 text-[#FF0000] font-mono px-3 py-2 text-sm"
          data-testid="album-type-input"
        >
          <option value="album">Album</option>
          <option value="ep">EP</option>
          <option value="single">Single</option>
          <option value="compilation">Compilation</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Status</label>
        <select
          value={value.status}
          onChange={(e) => onChange({ ...value, status: e.target.value })}
          className="w-full bg-black border border-[#FF0000]/30 text-[#FF0000] font-mono px-3 py-2 text-sm"
          data-testid="album-status-input"
        >
          <option value="released">Released</option>
          <option value="upcoming">Upcoming</option>
          <option value="pre-order">Pre-order</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Catalog #</label>
        <Input
          value={value.catalog_number || ""}
          onChange={(e) => onChange({ ...value, catalog_number: e.target.value })}
          placeholder="GMS-001"
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-catalog-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Tuning (Hz)</label>
        <Input
          type="number"
          step="0.1"
          value={value.tuning_hz}
          onChange={(e) => onChange({ ...value, tuning_hz: parseFloat(e.target.value) || 432 })}
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-tuning-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Buy URL</label>
        <Input
          value={value.buy_url || ""}
          onChange={(e) => onChange({ ...value, buy_url: e.target.value })}
          placeholder="https://..."
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono"
          data-testid="album-buy-url-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest">Description</label>
        <Textarea
          value={value.description || ""}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          placeholder="Liner notes, story, context..."
          className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono min-h-[80px]"
          data-testid="album-description-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-mono text-[#FF0000]/60 uppercase tracking-widest flex items-center gap-2">
          <ImageIcon className="w-3 h-3" /> Cover Art
        </label>
        <div className="flex items-center gap-3 mt-1">
          {value.cover_art_url ? (
            <img
              src={value.cover_art_url}
              alt="Cover preview"
              className="w-20 h-20 object-cover border border-[#FF0000]/40"
              data-testid="album-cover-preview"
            />
          ) : (
            <div className="w-20 h-20 border border-dashed border-[#FF0000]/30 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-[#FF0000]/30" />
            </div>
          )}
          <div className="flex-1 space-y-2">
            <Input
              value={value.cover_art_url || ""}
              onChange={(e) => onChange({ ...value, cover_art_url: e.target.value })}
              placeholder="Paste image URL or upload below"
              className="bg-black border-[#FF0000]/30 text-[#FF0000] font-mono text-xs"
              data-testid="album-cover-url-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => uploadCover(e.target.files?.[0])}
              data-testid="album-cover-file-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="border-[#FF0000]/40 text-[#FF0000] text-xs"
              data-testid="album-cover-upload-btn"
            >
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Upload Cover
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackRow({ albumId, track, onRemove }) {
  return (
    <div className="flex items-center gap-2 border border-[#FF0000]/15 bg-black/60 px-3 py-2" data-testid={`track-row-${track.id}`}>
      <span className="text-[10px] font-mono text-[#FF0000]/40 w-6 shrink-0">{track.track_number}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-mono text-[#FF0000] truncate">{track.title}</div>
        {track.audio_url && (
          <audio src={track.audio_url} controls className="w-full h-6 mt-1" style={{ maxHeight: 24 }} />
        )}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onRemove(track.id)}
        className="text-[#FF0000]/60 hover:text-[#FF0000] h-7 px-2"
        data-testid={`track-remove-${track.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function AddTrackForm({ albumId, onAdded, defaultNumber }) {
  const fileRef = useRef(null);
  const [track, setTrack] = useState({ ...EMPTY_TRACK, track_number: defaultNumber });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-audio`, fd, { withCredentials: true });
      setTrack((t) => ({ ...t, audio_url: res.data.url, title: t.title || file.name.replace(/\.[^/.]+$/, "") }));
      toast.success("Audio uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audio upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!track.title.trim()) {
      toast.error("Track title required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/discography/${albumId}/tracks`, track, { withCredentials: true });
      toast.success(`Track "${track.title}" added`);
      setTrack({ ...EMPTY_TRACK, track_number: (defaultNumber || 1) + 1 });
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-dashed border-[#FF0000]/30 bg-[#FF0000]/3 p-3 space-y-2" data-testid="add-track-form">
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={track.track_number}
          onChange={(e) => setTrack({ ...track, track_number: parseInt(e.target.value) || 1 })}
          className="w-16 bg-black border-[#FF0000]/30 text-[#FF0000] font-mono text-xs"
          data-testid="new-track-number"
        />
        <Input
          value={track.title}
          onChange={(e) => setTrack({ ...track, title: e.target.value })}
          placeholder="Track title"
          className="flex-1 bg-black border-[#FF0000]/30 text-[#FF0000] font-mono text-xs"
          data-testid="new-track-title"
        />
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={track.audio_url}
          onChange={(e) => setTrack({ ...track, audio_url: e.target.value })}
          placeholder="Audio URL (or upload)"
          className="flex-1 bg-black border-[#FF0000]/30 text-[#FF0000] font-mono text-xs"
          data-testid="new-track-audio-url"
        />
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => uploadAudio(e.target.files?.[0])}
          data-testid="new-track-audio-file"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="border-[#FF0000]/40 text-[#FF0000] text-xs h-8"
          data-testid="new-track-upload-btn"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Music className="w-3 h-3" />}
        </Button>
        <Button
          size="sm"
          disabled={saving}
          onClick={save}
          className="bg-[#FF0000] text-black hover:bg-[#FF0000]/90 text-xs h-8"
          data-testid="new-track-save-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
          Add
        </Button>
      </div>
    </div>
  );
}

function AlbumCard({ album, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(album);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setDraft(album); }, [album]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      const updates = {
        title: draft.title,
        artist: draft.artist,
        release_date: draft.release_date,
        cover_art_url: draft.cover_art_url,
        description: draft.description,
        genre: draft.genre,
        label: draft.label,
        catalog_number: draft.catalog_number,
        album_type: draft.album_type,
        status: draft.status,
        stream_urls: draft.stream_urls || {},
        buy_url: draft.buy_url,
        tags: draft.tags || [],
        tuning_hz: draft.tuning_hz,
      };
      await axios.put(`${API}/discography/${album.id}`, updates, { withCredentials: true });
      toast.success("Album updated");
      setEditing(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${album.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/discography/${album.id}`, { withCredentials: true });
      toast.success("Album deleted");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const removeTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/admin/discography/${album.id}/tracks/${trackId}`, { withCredentials: true });
      toast.success("Track removed");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  const tracks = (album.tracks || []).slice().sort((a, b) => (a.track_number || 0) - (b.track_number || 0));

  return (
    <Card className="bg-black/70 border-[#FF0000]/20" data-testid={`admin-album-${album.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {album.cover_art_url ? (
            <img src={album.cover_art_url} alt={album.title} className="w-16 h-16 object-cover border border-[#FF0000]/30 shrink-0" />
          ) : (
            <div className="w-16 h-16 border border-[#FF0000]/30 flex items-center justify-center shrink-0">
              <Disc3 className="w-6 h-6 text-[#FF0000]/40" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-left w-full"
              data-testid={`admin-album-toggle-${album.id}`}
            >
              {expanded ? <ChevronDown className="w-4 h-4 text-[#FF0000]/60" /> : <ChevronRight className="w-4 h-4 text-[#FF0000]/60" />}
              <div className="min-w-0">
                <div className="text-base font-bold text-[#FF0000] truncate">{album.title}</div>
                <div className="text-[10px] font-mono text-[#FF0000]/50 truncate">
                  {album.artist} · {album.status} · {tracks.length} tracks
                </div>
              </div>
            </button>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(!editing)}
              className="border-[#FF0000]/30 text-[#FF0000] text-xs h-7"
              data-testid={`admin-album-edit-${album.id}`}
            >
              {editing ? "Cancel" : "Edit"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={remove}
              disabled={deleting}
              className="text-[#FF0000]/60 hover:text-[#FF0000] h-7 px-2"
              data-testid={`admin-album-delete-${album.id}`}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {editing && (
          <div className="mt-4 space-y-3 border-t border-[#FF0000]/15 pt-4">
            <AlbumForm value={draft} onChange={setDraft} />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={saveEdits}
                disabled={saving}
                className="bg-[#FF0000] text-black hover:bg-[#FF0000]/90"
                data-testid={`admin-album-save-${album.id}`}
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        )}

        {expanded && (
          <div className="mt-4 space-y-2 border-t border-[#FF0000]/15 pt-4">
            <div className="text-[10px] font-mono text-[#FF0000]/50 uppercase tracking-widest mb-2">Tracks</div>
            {tracks.map((t) => (
              <TrackRow key={t.id} albumId={album.id} track={t} onRemove={removeTrack} />
            ))}
            {tracks.length === 0 && (
              <div className="text-xs font-mono text-[#FF0000]/30 text-center py-3">No tracks yet</div>
            )}
            <AddTrackForm
              albumId={album.id}
              defaultNumber={(tracks[tracks.length - 1]?.track_number || 0) + 1}
              onAdded={onRefresh}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminDiscography() {
  const { user, loading } = useAuth();
  const [data, setData] = useState({ db_albums: [], static_catalog: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newAlbum, setNewAlbum] = useState(EMPTY_ALBUM);
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/admin/discography`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchData();
  }, [user]);

  if (loading) {
    return <div className="p-8 text-center text-[#FF0000]/60 font-mono">Loading…</div>;
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/auth?return=/admin/discography" replace />;
  }

  const createAlbum = async () => {
    if (!newAlbum.title.trim()) {
      toast.error("Title required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/discography`, newAlbum, { withCredentials: true });
      toast.success(`"${newAlbum.title}" created`);
      setNewAlbum(EMPTY_ALBUM);
      setShowCreate(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-discography-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#FF0000] flex items-center gap-3" data-testid="admin-page-title">
            <Disc3 className="w-7 h-7" />
            DISCOGRAPHY ADMIN
          </h1>
          <p className="text-xs font-mono text-[#FF0000]/50 mt-1">
            Upload covers + tracks · Manage SOUND page catalog · Signed in as <span className="text-[#FF0000]">{user.email}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/sound"
            className="text-xs font-mono text-[#FF0000]/60 hover:text-[#FF0000] underline underline-offset-2"
            data-testid="admin-back-sound"
          >
            ← Back to SOUND
          </Link>
          <Button
            onClick={() => setShowCreate(!showCreate)}
            className="bg-[#FF0000] text-black hover:bg-[#FF0000]/90 font-mono"
            data-testid="admin-toggle-create-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {showCreate ? "Close" : "New Album"}
          </Button>
        </div>
      </div>

      {showCreate && (
        <Card className="bg-black/70 border-[#FF0000]/30" data-testid="admin-create-card">
          <CardContent className="p-4 space-y-4">
            <div className="text-sm font-mono text-[#FF0000] uppercase tracking-widest">Create New Album</div>
            <AlbumForm value={newAlbum} onChange={setNewAlbum} />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setNewAlbum(EMPTY_ALBUM); setShowCreate(false); }}
                className="border-[#FF0000]/30 text-[#FF0000]"
                data-testid="admin-create-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={createAlbum}
                disabled={creating}
                className="bg-[#FF0000] text-black hover:bg-[#FF0000]/90"
                data-testid="admin-create-save-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Album
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <div className="text-[10px] font-mono text-[#FF0000]/50 uppercase tracking-widest" data-testid="admin-db-section-title">
          DB Albums ({data.db_albums.length})
        </div>
        {loadingData && (
          <div className="flex items-center justify-center py-6 text-[#FF0000]/60">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="font-mono text-sm">Loading...</span>
          </div>
        )}
        {!loadingData && data.db_albums.length === 0 && (
          <div className="text-center py-8 text-[#FF0000]/40 font-mono text-sm border border-dashed border-[#FF0000]/15">
            No DB albums yet — click "New Album" to add one
          </div>
        )}
        {data.db_albums.map((album) => (
          <AlbumCard key={album.id} album={album} onRefresh={fetchData} />
        ))}
      </div>

      <div className="space-y-3">
        <div className="text-[10px] font-mono text-[#FF0000]/50 uppercase tracking-widest" data-testid="admin-static-section-title">
          Static Catalog ({data.static_catalog.length})
        </div>
        <div className="text-[9px] font-mono text-[#FF0000]/30 italic">
          These are baked into the SOUND page code. Use DB Albums above to add new uploads dynamically.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {data.static_catalog.map((a) => (
            <div key={a.key} className="flex items-center gap-2 border border-[#FF0000]/10 bg-black/40 p-2" data-testid={`admin-static-album-${a.key}`}>
              {a.cover_art_url && (
                <img src={a.cover_art_url} alt={a.title} className="w-10 h-10 object-cover border border-[#FF0000]/20 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-xs text-[#FF0000] truncate">{a.title}</div>
                <div className="text-[9px] font-mono text-[#FF0000]/40">{a.track_count} tracks</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
